import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {FormControl, FormGroup} from "@angular/forms";
import {EAction} from "../../../shared/enums/EAction";
import {ValidationHelperService} from "../../../core/helpers/validation-helper.service";
import {IPairedExerciseForm} from "../../../shared/interfaces/forms/exercises/IPairedExerciseForm";
import {IRegularExerciseForm} from "../../../shared/interfaces/forms/exercises/IRegularExerciseForm";
import {
  PairedExerciseHelper,
  PairedExerciseSide,
} from "../../../core/forms/helpers/PairedExerciseHelper";
import {NotificationService} from "../../../core/services/notification.service";
import {IPairedExercise, IPairedExerciseConfig} from "../save-paired-exercise/save-paired-exercise.component";
import {ExerciseHelper} from "../../../core/helpers/exercise.helper";
import {PairedExerciseFormFactory} from "../../../core/forms/factory/PairedExerciseFormFactory";
import {Subscription} from "rxjs";
import {distinctUntilChanged, skip, startWith} from "rxjs/operators";

@Component({
  selector: "app-paired-exercise-form",
  templateUrl: "./paired-exercise-form.component.html",
  styleUrls: ["./paired-exercise-form.component.scss"],
})
export class PairedExerciseFormComponent implements OnInit, OnDestroy {
  @Input() _formGroup: FormGroup<IPairedExerciseForm>;
  @Input() action: EAction;
  @Input() exercise: IPairedExercise;
  @Input() isSubmitting = false;
  @Output() _submit = new EventEmitter();
  @Output() uploadingChange = new EventEmitter<boolean>();

  configFormGroup = new FormGroup({
    selectedConfig: new FormControl(),
  });

  protected readonly ValidationHelperService = ValidationHelperService;
  readonly pairedConfigAction = EAction.Edit;
  selectedExerciseIndex = 0;
  private uploadStates = {
    firstVideo: false,
    firstImage: false,
    secondVideo: false,
    secondImage: false,
  };
  private readonly subscriptions = new Subscription();
  private isSynchronizing = false;

  constructor(
      private notificationService: NotificationService,
      private exerciseHelper: ExerciseHelper
  ) {}

  ngOnInit(): void {
    this.synchronizeIdentity();
    this.subscriptions.add(this.type.valueChanges.pipe(
      startWith(this.type.value),
      distinctUntilChanged(),
      skip(1),
    ).subscribe((type: number) => {
      this.withSynchronizationPaused(() => {
        this.synchronizeIdentity();
        if (this.action === EAction.Add) {
          PairedExerciseFormFactory.resetTypeConfiguration(
            this._formGroup,
            type,
          );
        }
      });
    }));
    this.subscriptions.add(
      this.category.valueChanges.subscribe(() =>
        this.withSynchronizationPaused(() => this.synchronizeIdentity()),
      ),
    );
    this.subscriptions.add(
      this.firstExercise.controls.config.valueChanges.subscribe(() =>
        this.synchronizeFrom("first"),
      ),
    );
    this.subscriptions.add(
      this.secondExercise.controls.config.valueChanges.subscribe(() =>
        this.synchronizeFrom("second"),
      ),
    );
    if(this.action === EAction.Edit) {
      this.configFormGroup.controls.selectedConfig.setValue(
        this.exercise.pairedExerciseConfigs.find(
          config => config.id === this.exercise.defaultConfigId,
        ),
        {emitEvent: false},
      );
      this.subscriptions.add(this.configFormGroup.controls.selectedConfig.valueChanges.subscribe((data) => {
        if (!data) return;
        this.exercise.defaultConfigId = data.id;
        this.withSynchronizationPaused(() =>
          PairedExerciseHelper.patchFormConfig(
            this._formGroup,
            this.exercise,
            this.selectedExerciseSide,
          ),
        );
      }));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submitForm() {
    if (this.isSubmitting || this.isUploading) {
      this.notificationService.warn(
        this.isUploading ? "Please wait for media uploads to finish." : "Exercise is already being saved.",
      );
      return;
    }
    PairedExerciseHelper.mirrorRequiredConfig(
      this._formGroup,
      this.selectedExerciseSide,
    );
    if (!PairedExerciseHelper.isFormValid(this._formGroup)) {
      this._formGroup.markAllAsTouched();
      this.notificationService.error("The form is invalid. Please check.");
      return;
    }
    this.emitSubmit();
  }

  updateUploadState(
    key: "firstVideo" | "firstImage" | "secondVideo" | "secondImage",
    uploading: boolean,
  ): void {
    this.uploadStates[key] = uploading;
    this.uploadingChange.emit(this.isUploading);
  }

  get isUploading(): boolean {
    return Object.values(this.uploadStates).some(Boolean);
  }

  onSelectedExerciseIndexChange(index: number): void {
    this.selectedExerciseIndex = index;
  }

  emitSubmit(): void {
    this._submit.emit(
      PairedExerciseHelper.createPayload(this._formGroup, this.exercise),
    );
  }

  getConfigTitle(config: IPairedExerciseConfig): string {
      const firstExerciseConfig = this.exercise.firstExercise.configs.find(c => c.id === config.firstConfigId);
      const secondExerciseConfig = this.exercise.secondExercise.configs.find(c => c.id === config.secondConfigId);
      return `${this.exercise.firstExercise.name} | ${this.exerciseHelper.getConfigTitle(this.exercise.type, firstExerciseConfig)} \n | ${this.exercise.secondExercise.name} | ${this.exerciseHelper.getConfigTitle(this.exercise.type, secondExerciseConfig)}`
  }

  get firstExercise(): FormGroup<IRegularExerciseForm> {
    return this._formGroup.controls["firstExercise"] as FormGroup;
  }

  get secondExercise(): FormGroup<IRegularExerciseForm> {
    return this._formGroup.controls["secondExercise"] as FormGroup;
  }

  get type(): FormControl {
    return this._formGroup.controls["type"] as FormControl;
  }

  get name(): FormControl {
    return this._formGroup.controls["name"] as FormControl;
  }

  get category(): FormControl {
    return this._formGroup.controls["category"] as FormControl;
  }

  private get selectedExerciseSide(): PairedExerciseSide {
    return this.selectedExerciseIndex === 1 ? "second" : "first";
  }

  private synchronizeFrom(sourceSide: PairedExerciseSide): void {
    if (this.isSynchronizing) return;
    this.withSynchronizationPaused(() =>
      PairedExerciseHelper.mirrorRequiredConfig(
        this._formGroup,
        sourceSide,
      ),
    );
  }

  private synchronizeIdentity(): void {
    const type = this.type.value;
    const category = this.category.value;
    this.firstExercise.controls.type.setValue(type, {emitEvent: false});
    this.secondExercise.controls.type.setValue(type, {emitEvent: false});
    this.firstExercise.controls.category.setValue(category, {emitEvent: false});
    this.secondExercise.controls.category.setValue(category, {
      emitEvent: false,
    });
  }

  private withSynchronizationPaused<T>(action: () => T): T {
    const wasSynchronizing = this.isSynchronizing;
    this.isSynchronizing = true;
    try {
      return action();
    } finally {
      this.isSynchronizing = wasSynchronizing;
    }
  }

  protected readonly EAction = EAction;
}
